/**
 * The two delivery gaps in the `__gpLogs` channel, fixed in one place (#1345,
 * #1347 — see docs/superpowers/specs/2026-09-12-browser-telemetry-api-design.md
 * sections A and B).
 *
 * 1. The attestation token gating anonymous logging lives five minutes and was
 *    minted once per render. The home app is hash-routed, so `/#/signup` does
 *    not re-render: one token covered a whole anonymous session, and everything
 *    past minute five was discarded by the hub without an error. This renews it.
 *
 * 2. `logging.ts`'s pump is a 1s timer that waits on a SignalR handshake, so
 *    nothing queued when the page hides survives. This drains what is left
 *    through `navigator.sendBeacon`.
 *
 * Framework-agnostic on purpose: no jQuery, no AngularJS, nothing touched at
 * module scope — which is what lets the spec drive it with no browser at all.
 * `app-common/client-alerts.ts` is the model and carries the same constraint;
 * `logging.ts`'s own `$(() => …)` bootstrap is precisely why it cannot be
 * imported outside a browser.
 */

const DEFAULT_TOKEN_URL = '/api/gameplan/client-log-token';
const DEFAULT_LOGS_URL = '/api/gameplan/client-logs';

/**
 * ClientLogAttestation.Lifetime is five minutes and stays that way — #964 chose
 * it so a captured token is useless quickly.
 *
 * Three rather than four: a backgrounded tab's timers are throttled, and at four
 * minutes a throttled renewal can land on the expiry itself. Two minutes of slack
 * absorbs that and a slow round trip both.
 */
const RENEW_AFTER_MS = 3 * 60 * 1000;

/**
 * A failed renewal retries here instead of waiting a full cycle. At the normal
 * interval one blip — a flaky network, a redeploy — left an anonymous page deaf
 * for minutes past expiry, which is the exact loss renewal exists to prevent.
 */
const RETRY_AFTER_MS = 30 * 1000;

/** One queued `__gpLogs` row: [when, messageTemplate, args, extraProperties]. */
export type QueuedLogEntry = any[];

export interface LogTransportDeps {
    tokenUrl?: string;
    logsUrl?: string;
    /** Fetches a fresh attestation token. */
    fetchTokenImpl?: (url: string) => Promise<string>;
    /** Ordinary send. */
    sendImpl?: (url: string, body: string) => void;
    /** Unload-safe send. Returns false when unavailable. */
    beaconImpl?: (url: string, body: string) => boolean;
    setTimeoutImpl?: (handler: () => void, timeout: number) => number;
    /** Registers the unload flush. Omitted outside a browser. */
    onPageHide?: (handler: () => void) => void;
}

export interface LogTransport {
    /** The attestation token to present with a log batch. */
    token(): string;
    /** Seed from the page's `gpLogSetup` command. */
    seedToken(token: string): void;
    /** Begin periodic renewal. Idempotent. */
    start(): void;
    /** Drain `queued()` through the beacon when the page hides. Idempotent. */
    hookUnload(queued: () => QueuedLogEntry[], sessionId: () => string): void;
    /** Drain now. Exposed for the unload path and for tests. */
    flushNow(viaBeacon?: boolean): void;
}

export function createLogTransport(deps: LogTransportDeps = {}): LogTransport {
    const tokenUrl = deps.tokenUrl ?? DEFAULT_TOKEN_URL;
    const logsUrl = deps.logsUrl ?? DEFAULT_LOGS_URL;
    const fetchToken = deps.fetchTokenImpl ?? defaultFetchToken;
    const send = deps.sendImpl ?? defaultSend;
    const beacon = deps.beaconImpl ?? defaultBeacon;
    const schedule = deps.setTimeoutImpl
        ?? ((handler: () => void, delay: number) => setTimeout(handler, delay) as unknown as number);
    const onPageHide = deps.onPageHide ?? defaultOnPageHide;

    let currentToken = '';
    let started = false;
    let unloadHooked = false;
    let queued: () => QueuedLogEntry[] = () => [];
    let sessionId: () => string = () => '';

    function scheduleRenewal(delay: number): void {
        schedule(() => {
            fetchToken(tokenUrl).then(
                fresh => {
                    // An empty answer is not an improvement on what we hold, and
                    // is treated as a failure for scheduling: come back soon.
                    if (fresh) currentToken = fresh;
                    scheduleRenewal(fresh ? RENEW_AFTER_MS : RETRY_AFTER_MS);
                },
                () => {
                    // Offline, or the endpoint is down. Keep the token we have —
                    // it may still be inside its five minutes, and replacing it
                    // with nothing guarantees the loss this exists to prevent.
                    scheduleRenewal(RETRY_AFTER_MS);
                });
        }, delay);
    }

    function flushNow(viaBeacon = false): void {
        // The try spans the WHOLE body, not just the send. `entries` are raw
        // __gpLogs rows whose args and extras come from arbitrary call sites, so
        // JSON.stringify can throw on a circular reference or a getter that
        // throws — logging.ts:162 already wraps its own stringify for exactly
        // that reason. A throw escaping here aborts the rest of the pagehide
        // handler, which is the one thing this module must never do.
        try {
            const entries = queued();
            if (!entries || entries.length === 0) return;

            const body = JSON.stringify({
                loggingKey: currentToken,
                // Written server-side into the ClientConnectionId property,
                // because GamePlanLogAnalyser's ConnectionMap keys on that and
                // drops a row it cannot tie to a session.
                sessionId: sessionId(),
                entries: entries,
            });

            if (viaBeacon && beacon(logsUrl, body)) return;
            send(logsUrl, body);
        } catch {
            // Swallowed by design: losing a batch beats breaking the page.
        }
    }

    // A closure function rather than a method on the returned object: the
    // pagehide handler below would otherwise depend on `this`, which is one
    // refactor away from being wrong in a path that has no test coverage in
    // production and fails silently.
    return {
        token: () => currentToken,

        seedToken(token: string): void {
            if (token) currentToken = token;
        },

        start(): void {
            if (started) return;
            started = true;
            scheduleRenewal(RENEW_AFTER_MS);
        },

        hookUnload(queuedImpl: () => QueuedLogEntry[], sessionIdImpl: () => string): void {
            queued = queuedImpl;
            sessionId = sessionIdImpl;
            if (unloadHooked) return;
            unloadHooked = true;
            // Registered on first use rather than at module scope, so importing
            // this module never touches the DOM.
            onPageHide(() => flushNow(true));
        },

        flushNow,
    };
}

function defaultFetchToken(url: string): Promise<string> {
    if (typeof fetch !== 'function') return Promise.resolve('');
    return fetch(url, { method: 'GET', credentials: 'same-origin' })
        .then(r => (r.ok ? r.json() : null))
        .then(body => (body && typeof body.loggingKey === 'string' ? body.loggingKey : ''));
}

function defaultSend(url: string, body: string): void {
    if (typeof fetch !== 'function') return;
    // keepalive so a flush racing a navigation still leaves the tab.
    void fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        credentials: 'same-origin',
        keepalive: true,
    }).catch(() => { /* swallow */ });
}

/**
 * The Blob type matters: it sets the request's Content-Type, and the WebRole
 * controller binds a JSON body. sendBeacon cannot set an Authorization header,
 * which is why both hosts fall back to the latestgameplantoken cookie.
 */
function defaultBeacon(url: string, body: string): boolean {
    const nav = typeof navigator !== 'undefined' ? navigator : undefined;
    if (!nav || typeof nav.sendBeacon !== 'function') return false;
    try {
        return nav.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } catch {
        return false;
    }
}

function defaultOnPageHide(handler: () => void): void {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
    // pagehide, not beforeunload: beforeunload is ignored on mobile Safari and
    // disqualifies the page from the back/forward cache.
    window.addEventListener('pagehide', handler);
}
